import { report } from '../../services/report';
import { questionAttributes } from '../../features/questions/common/telemetry';
import { pagedVisibility, phaseRange } from './domain/paging';

export function buildResearchDefenderPayload(
  object,
  text,
  c_text,
  t_page_load,
  t_text_typed,
  t_submit
) {
  const { input_id: q_id, survey_id: sy_nr, visa: sn_ud } = object;
  return JSON.stringify({
    q_id,
    text,
    c_text,
    t_page_load,
    t_text_typed,
    t_submit,
    sn_ud,
    sy_nr,
  });
}

export function getDefaultPhaseRange(self) {
  // Arithmetic lives in the domain layer; this reads the two fields off the
  // widget and passes them in. See `./domain/paging.js` `phaseRange`.
  return phaseRange({
    phase_pos: self._object.phase_pos,
    displaymax: self._object.displaymax,
  });
}

/**
 *
 * `displaymax` counts response options, while the elements being paged are
 * rows holding `columns` options each, so an item range only matches a row
 * range when there is a single column. The layout records which rows belong
 * to which page; prefer that, and fall back to the item range for widgets
 * that page over the response options themselves (cardsort).
 *
 * @param {object} self The widget instance.
 * @param {object} [layout] The widget's DynamicRowLayout instance.
 */
export function getPhaseRange(self, layout) {
  var ranges = layout && layout.groupRowRanges;
  if (!ranges || !ranges.length) {
    return getDefaultPhaseRange(self);
  }

  var range = ranges[self._object.phase_pos] || ranges[ranges.length - 1];
  return { min: range.start, max: range.end };
}

export function getLoadAccessibilityMenuTrigger() {
  return {
    button: {
      keydown: true,
      click: false,
    },
    menu: {
      keydown: true,
      click: true,
      mouseover: true,
    },
  };
}

export function handlePagedVisibility(elems, range) {
  // The domain rule decides which indices show and which hide; the widget then
  // applies that to its elements. See `./domain/paging.js` `pagedVisibility`.
  var visibility = pagedVisibility(elems.length, range);
  visibility.hide.forEach(function (idx) {
    $(elems[idx]).hide();
  });
  visibility.show.forEach(function (idx) {
    $(elems[idx]).show();
  });
}

const REPORTED_FIELDS = [
  'type',
  'request',
  'code',
  'fileName',
  'lineNumber',
  'columnNumber',
  'url',
  'error',
];

/**
 * Report a failure on the React feature path (the ReactFeatures chunk failed to
 * download, or the feature threw while its widget was built), silently, as a copy:
 * a DOMException's name is read-only. Never throws, so the fallback always runs.
 * The copy carries the question's New Relic attributes (`telemetryAttributes`,
 * merged by `report.logError`), tagged with the legacy render path it falls
 * back to.
 */
export const reportChunkLoadFailure = (err, view, question) => {
  try {
    const isError = err instanceof Error;
    let text;
    try {
      text = isError ? err.message : String(err);
    } catch (e) {
      text = Object.prototype.toString.call(err);
    }
    const copy = new Error(
      'React feature "' + view + '" failed to load: ' + text
    );
    copy.name = isError ? err.name : 'ChunkLoadError';
    if (isError) {
      copy.stack = err.stack;
      REPORTED_FIELDS.forEach((field) => {
        if (err[field] !== undefined) {
          copy[field] = err[field];
        }
      });
    }
    if (err && err.cause) {
      copy.cause = err.cause;
    } else if (copy.name !== 'ChunkLoadError') {
      copy.cause = 'reactFallback';
    }
    copy.telemetryAttributes = questionAttributes(question, 'legacy');
    report.logError(copy, true);
  } catch (e) {}
};

export function resolvePagedElems(self, elems) {
  var resolved = elems || self.$el.find('.dynamic-row');
  return resolved.length === 0 ? null : resolved;
}
